import {Shift} from '../../features/shifts/types';
import {
    CreateShift,
    TimeSheetItem,
    TimeSheetItemStatus, TimeSheetItemType,
    TimeSheetItemUpdateState
} from '../../features/schedule/types';
import {Absence} from '../../features/absences/types';
import {PIdNameModel} from '../../features/projects/types';
import {REmployees} from '../../features/roles/types';
import {v4 as uuidv4} from 'uuid';
import {getAbsenceStatus, getShiftStatus} from '../../helpers/ScheduleHelper';
import {AutoCompleteItemValue} from '../../components/shared/nextgen/selector/types';
import {AbsenceBehavior} from '../../features/types/types';

export const convertTimeshiftUpdateStateToShift = (updateState: TimeSheetItemUpdateState, shifts: Shift[]): Shift => {
    const shift = shifts.find((s) => s.id === updateState.timesheetId);
    if (!shift) {
        return <Shift>{
            approved: false,
            billable: false,
            comment: '',
            note: '',
            date: '',
            employeeId: 0,
            enteredHoursString: '',
            hours: 0,
            id: 0,
            isOvertime: false,
            project: {},
            recurringPeriod: 0,
            remote: false,
            shiftType: {}
        }
    }

    return <Shift>{
        id: shift.id,
        employeeId: shift.employeeId,
        shiftType: shift.shiftType,
        hours: shift.hours,
        comment: shift.comment,
        note: shift.note,
        date: shift.date,
        enteredHoursString: shift.enteredHoursString,
        isOvertime: shift.isOvertime,
        project: shift.project,
        recurringPeriod: shift.recurringPeriod,
        remote: shift.remote,
        approved: updateState.newStatus === TimeSheetItemStatus.APPROVED || updateState.newStatus === TimeSheetItemStatus.BILLABLE,
        billable: updateState.newStatus === TimeSheetItemStatus.BILLABLE
    }
}

export const convertTimeshiftUpdateStateToAbsence = (updateState: TimeSheetItemUpdateState, absences: Absence[]): Absence => {
    const absence = absences.find((s) => s.id === updateState.timesheetId);
    if (!absence) {
        return <Absence>{
            approved: false,
            comment: '',
            note: '',
            date: '',
            employeeId: 0,
            enteredHoursString: '',
            hours: 0,
            id: 0,
            absenceType: {
                id: 0,
                name: '',
                behavior: AbsenceBehavior.None,
                active: false,
                default: false
            },
            behavior: AbsenceBehavior.None,
        }
    }

    return <Absence>{
        id: absence.id,
        employeeId: absence.employeeId,
        hours: absence.hours,
        comment: absence.comment,
        note: absence.note,
        date: absence.date,
        enteredHoursString: absence.enteredHoursString,
        approved: updateState.newStatus === TimeSheetItemStatus.APPROVED,
        absenceType: absence.absenceType,
        behavior: absence.behavior
    }
}

export const isCreateShift = (obj: any): obj is CreateShift => {
    return obj != undefined && 'employees' in obj;
}

export const isEditShift = (obj: any): obj is Shift => {
    return obj != undefined && 'employeeId' in obj && 'shiftType' in obj;
}

export const isEditAbsence = (obj: any): obj is Absence => {
    return obj != undefined && 'employeeId' in obj && 'absenceType' in obj;
}

export const createEmptyTimesheetItem = (employee?: REmployees, project?: PIdNameModel): TimeSheetItem => ({
    id: '',
    employeeId: employee?.id ?? 0,
    employeeName: employee?.name ?? '',
    date: '',
    targetName: project?.name ?? '',
    targetId: project?.id ?? 0,
    targetProjectTypeId: 0,
    isBillable: false,
    note: '',
    internalNote: '',
    hours: 0,
    status: TimeSheetItemStatus.NORMAL,
    timeshiftTypeId: 0,
    timeshiftType: TimeSheetItemType.SHIFT,
    itemId: 0
})

export const shiftAsTimeSheetItem = (shift: Shift, employees: REmployees[]): TimeSheetItem => {
    let e = employees.find((x) => x.id === shift.employeeId);
    if (!e) {
        console.warn('Employee not found for shift', shift.employeeId, shift);
        e = {id: shift.employeeId, name: ''} as REmployees;
    }
    return {
        id: shift.id.toString(),
        employeeId: e.id,
        employeeName: e.name,
        targetId: shift.project.id,
        targetName: shift.project.name,
        targetProjectTypeId: shift.project.projectType?.id ?? 0,
        isBillable: shift.project.isBillable,
        date: shift.date,
        note: shift.comment,
        hours: shift.hours,
        timeBankHours: shift.timeBankHours,
        status: getShiftStatus(shift),
        internalNote: shift.note,
        timeshiftTypeId: shift.shiftType.id,
        timeshiftType: TimeSheetItemType.SHIFT,
        itemId: shift.id
    }
}

export const absenceAsTimeSheetItem = (absence: Absence, employees: REmployees[]): TimeSheetItem | null => {
    if (!absence.absenceType) {
        console.warn('Absence type not found for absence', absence.id, absence);
        return null;
    }

    let e = employees.find((x) => x.id === absence.employeeId);
    if (!e) {
        console.warn('Employee not found for absence', absence.employeeId, absence);
        e = {id: absence.employeeId, name: ''} as REmployees;
    }

    return {
        id: absence.id.toString(),
        employeeId: e.id,
        employeeName: e.name,
        targetId: absence.absenceType.id,
        targetName: absence.absenceType.name,
        targetProjectTypeId: 0,
        isBillable: false,
        date: absence.date,
        note: absence.comment,
        hours: absence.hours,
        timeBankHours: absence.timeBankHours,
        status: getAbsenceStatus(absence.approved),
        internalNote: absence.note,
        timeshiftTypeId: absence.absenceType.id,
        timeshiftType: TimeSheetItemType.ABSENCE,
        itemId: absence.id,
        behavior: absence.behavior ?? AbsenceBehavior.None
    }
}

export const containsStatus = (statuses: AutoCompleteItemValue[], status: TimeSheetItemStatus): boolean =>
    !!statuses.find((x) => x.originalId === status);
